<div class="d-flex justify-content-between align-items-center mb-6">
  <ul class="nav nav-tabs nav-line-tabs fs-6">
    <li class="nav-item">
      <a class="nav-link" [class.active]="selectedTab === 'personal'" (click)="selectedTab = 'personal'">
        <i class="fa fa-user me-2"></i>{{ 'userProfile.myEvents.tabs.personal' | translate }}
        <span class="badge badge-light-primary ms-2">{{ personalEvents.length }}</span>
      </a>
    </li>
    <li class="nav-item">
      <a class="nav-link" [class.active]="selectedTab === 'invited'" (click)="selectedTab = 'invited'">
        <i class="fa fa-users me-2"></i>{{ 'userProfile.myEvents.tabs.invited' | translate }}
        <span class="badge badge-light-primary ms-2">{{ invitedEvents.length }}</span>
      </a>
    </li>
  </ul>
  @if (selectedTab === 'personal') {
    <app-button
      [variant]="'primary'"
      [label]="'userProfile.myEvents.hostEvent' | translate"
      [iconClass]="'fa fa-plus'"
      [disabled]="hostingEvent"
      [loading]="hostingEvent"
      (clicked)="hostEvent()"
    />
  }
</div>

@if (loading) {
  <div class="text-center py-10">
    <i class="fa fa-spinner fa-spin fs-3 text-muted"></i>
    <p class="text-muted mt-3">{{ 'userProfile.myEvents.loading' | translate }}</p>
  </div>
} @else if (selectedTab === 'personal') {
  <div class="row mb-9">
    <!-- Events Column -->
    <div class="col-lg-8">
      @if (personalEvents.length === 0) {
        <div class="text-center py-10">
          <i class="fa fa-calendar-plus fs-3x text-muted mb-5"></i>
          <h4 class="text-muted">{{ 'userProfile.myEvents.noPersonalEvents' | translate }}</h4>
          <p class="text-muted">{{ 'userProfile.myEvents.noPersonalEventsHint' | translate }}</p>
        </div>
      } @else {
        <div class="row g-6">
          @for (membership of personalEvents; track membership.event.event_id) {
            <div class="col-md-6">
              <app-event-card
                [event]="membership.event"
                [showDelete]="false"
              />
            </div>
          }
        </div>
      }
    </div>

    <!-- Payment Settings Column -->
    <div class="col-lg-4">
      <div class="sticky-top" style="top: 80px;">
        <h5 class="fw-bold mb-4">
          <i class="fa fa-credit-card me-2"></i>{{ 'userProfile.myEvents.payment.title' | translate }}
        </h5>

        @if (loadingPayment) {
          <div class="text-center py-5">
            <i class="fa fa-spinner fa-spin fs-3 text-muted"></i>
          </div>
        } @else {
          <!-- Stripe Connect Card -->
          <div class="card mb-4">
            <div class="card-header border-0 py-4">
              <h6 class="card-title m-0 fs-7">
                <i class="fab fa-stripe me-2"></i>Stripe
              </h6>
              <span class="badge {{ getStatusBadgeClass() }} fs-9">{{ getStatusLabel() }}</span>
            </div>
            <div class="card-body pt-0">
              <!-- Not Connected -->
              @if (!connectStatus || connectStatus.status === 'not_connected') {
                <p class="text-muted fs-7 mb-3">
                  {{ 'userProfile.myEvents.payment.connectDescription' | translate }}
                </p>

                <div class="d-flex gap-2 mb-3">
                  <div
                    class="flex-fill card cursor-pointer border-2 mb-0"
                    [class.border-primary]="selectedBusinessType === 'individual'"
                    [class.bg-light-primary]="selectedBusinessType === 'individual'"
                    (click)="selectedBusinessType = 'individual'"
                  >
                    <div class="card-body text-center p-2">
                      <i class="fa fa-user mb-1" [class.text-primary]="selectedBusinessType === 'individual'"></i>
                      <div class="fs-8 fw-semibold">{{ 'userProfile.myEvents.payment.individual' | translate }}</div>
                    </div>
                  </div>
                  <div
                    class="flex-fill card cursor-pointer border-2 mb-0"
                    [class.border-primary]="selectedBusinessType === 'company'"
                    [class.bg-light-primary]="selectedBusinessType === 'company'"
                    (click)="selectedBusinessType = 'company'"
                  >
                    <div class="card-body text-center p-2">
                      <i class="fa fa-building mb-1" [class.text-primary]="selectedBusinessType === 'company'"></i>
                      <div class="fs-8 fw-semibold">{{ 'userProfile.myEvents.payment.business' | translate }}</div>
                    </div>
                  </div>
                </div>

                <div class="d-flex justify-content-center">
                  <app-button
                    [variant]="'primary'"
                    [label]="'userProfile.myEvents.payment.connectStripe' | translate"
                    [iconClass]="'fab fa-stripe'"
                    [loading]="actionLoading"
                    [size]="'md'"
                    (clicked)="startOnboarding()"
                  />
                </div>
              }

              <!-- Onboarding In Progress -->
              @if (connectStatus?.status === 'onboarding' || connectStatus?.status === 'pending') {
                <div class="d-flex align-items-start mb-3">
                  <i class="fa fa-clock text-warning me-2 mt-1"></i>
                  <p class="text-muted fs-7 mb-0">{{ 'userProfile.myEvents.payment.setupDescription' | translate }}</p>
                </div>
                <app-button
                  [variant]="'primary'"
                  [label]="'userProfile.myEvents.payment.continueSetup' | translate"
                  [iconClass]="'fa fa-arrow-right'"
                  [loading]="actionLoading"
                  [size]="'sm'"
                  class="w-100"
                  (clicked)="continueOnboarding()"
                />
              }

              <!-- Connected -->
              @if (connectStatus?.status === 'active') {
                <div class="d-flex align-items-start mb-3">
                  <i class="fa fa-check-circle text-success me-2 mt-1"></i>
                  <p class="text-muted fs-7 mb-0">{{ 'userProfile.myEvents.payment.stripeActiveDescription' | translate }}</p>
                </div>
                <div class="row g-2 mb-3">
                  <div class="col-6">
                    <div class="bg-light rounded p-2 text-center">
                      <div class="text-muted fs-9">{{ 'userProfile.myEvents.payment.chargesEnabled' | translate }}</div>
                      <div class="fw-bold fs-8">
                        @if (connectStatus?.charges_enabled) {
                          <i class="fa fa-check-circle text-success"></i>
                        } @else {
                          <i class="fa fa-times-circle text-danger"></i>
                        }
                      </div>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="bg-light rounded p-2 text-center">
                      <div class="text-muted fs-9">{{ 'userProfile.myEvents.payment.payoutsEnabled' | translate }}</div>
                      <div class="fw-bold fs-8">
                        @if (connectStatus?.payouts_enabled) {
                          <i class="fa fa-check-circle text-success"></i>
                        } @else {
                          <i class="fa fa-times-circle text-danger"></i>
                        }
                      </div>
                    </div>
                  </div>
                </div>
                <app-button
                  [variant]="'neutral'"
                  [label]="'userProfile.myEvents.payment.openDashboard' | translate"
                  [iconClass]="'fa fa-external-link-alt'"
                  [loading]="actionLoading"
                  [size]="'sm'"
                  class="w-100"
                  (clicked)="openDashboard()"
                />
              }

              <!-- Restricted -->
              @if (connectStatus?.status === 'restricted') {
                <div class="d-flex align-items-start mb-3">
                  <i class="fa fa-exclamation-triangle text-danger me-2 mt-1"></i>
                  <p class="text-muted fs-7 mb-0">{{ 'userProfile.myEvents.payment.restrictedDescription' | translate }}</p>
                </div>
                <app-button
                  [variant]="'danger'"
                  [label]="'userProfile.myEvents.payment.resolveIssues' | translate"
                  [iconClass]="'fa fa-external-link-alt'"
                  [loading]="actionLoading"
                  [size]="'sm'"
                  class="w-100"
                  (clicked)="openDashboard()"
                />
              }
            </div>
          </div>

          <!-- Custom Payment Card -->
          @if (!isStripeConnected()) {
            <div class="card">
              <div class="card-header border-0 py-4">
                <h6 class="card-title m-0 fs-7">
                  <i class="fa fa-money-bill-wave me-2"></i>{{ 'userProfile.myEvents.payment.customPaymentTitle' | translate }}
                </h6>
              </div>
              <div class="card-body pt-0">
                <p class="text-muted fs-7 mb-3">
                  {{ 'userProfile.myEvents.payment.customPaymentDescription' | translate }}
                </p>

                <div class="form-check form-switch mb-3">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="customPaymentEnabled"
                    [(ngModel)]="customPaymentEnabled"
                  />
                  <label class="form-check-label fw-semibold fs-7" for="customPaymentEnabled">
                    {{ 'userProfile.myEvents.payment.enableCustomPayment' | translate }}
                  </label>
                </div>

                @if (customPaymentEnabled) {
                  <div class="mb-3">
                    <app-input-component
                      [label]="'userProfile.myEvents.payment.paymentMethodName' | translate"
                      [(value)]="customPaymentMethodName"
                      [placeholder]="'userProfile.myEvents.payment.paymentMethodNamePlaceholder' | translate"
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold fs-7">
                      {{ 'userProfile.myEvents.payment.paymentInstructions' | translate }}
                    </label>
                    <textarea
                      class="form-control form-control-sm"
                      rows="3"
                      [(ngModel)]="customPaymentInstructions"
                      [placeholder]="'userProfile.myEvents.payment.paymentInstructionsPlaceholder' | translate"
                    ></textarea>
                  </div>

                  <div class="d-flex justify-content-center">
                    <app-button
                      [variant]="'primary'"
                      [label]="'userProfile.myEvents.payment.saveCustomPayment' | translate"
                      [iconClass]="'fa fa-save'"
                      [loading]="savingCustomPayment"
                      [size]="'md'"
                      (clicked)="saveCustomPaymentSettings()"
                    />
                  </div>
                }
              </div>
            </div>
          }
        }
      </div>
    </div>
  </div>
} @else if (selectedTab === 'invited') {
  @if (invitedEvents.length === 0) {
    <div class="text-center py-10">
      <i class="fa fa-users fs-3x text-muted mb-5"></i>
      <h4 class="text-muted">{{ 'userProfile.myEvents.noInvitedEvents' | translate }}</h4>
      <p class="text-muted">{{ 'userProfile.myEvents.noInvitedEventsHint' | translate }}</p>
    </div>
  } @else {
    <div class="row g-6 g-xl-9 mb-9">
      @for (membership of invitedEvents; track membership.member_id) {
        <div class="col-md-6 col-xl-4">
          <app-event-card
            [event]="membership.event"
            [showDelete]="false"
          />
        </div>
      }
    </div>
  }
}
